import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Crown, Menu, X } from "lucide-react";
import { useState } from "react";
import { DailyLoginRewards } from "@/components/DailyLoginRewards";

const links = [
  { to: "/", label: "Play" },
  { to: "/archive", label: "Archive" },
  { to: "/leaderboard", label: "Ranks" },
  { to: "/achievements", label: "Badges" },
  { to: "/community", label: "Community" },
  { to: "/about", label: "About" },
  { to: "/how-to-play", label: "How to Play" },
];

export function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <nav className="sticky top-0 z-50 bg-black/70 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <span className="bg-primary text-primary-foreground font-display text-lg px-2 py-0.5 leading-none">HHKD</span>
          
        </Link>

        <div className="hidden xl:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().split(" ").join("-")}`}
              className={`font-mono-caps text-xs px-4 py-2 transition-colors ${
                location.pathname === l.to ? "text-primary" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-3">
          {user ? (
            <>
              <DailyLoginRewards key={user.user_id} userId={user.user_id} />
              {!user.is_premium && (
                <Link to="/premium" data-testid="nav-premium-btn" className="hidden sm:flex items-center gap-1.5 bg-primary text-primary-foreground font-mono-caps text-[10px] px-3 py-2 hover-lift border border-primary">
                  <Crown size={13} /> Go Premium
                </Link>
              )}
              {user.is_premium && (
                <span className="hidden sm:flex items-center gap-1.5 text-primary font-mono-caps text-[10px] border border-primary/40 px-3 py-2">
                  <Crown size={13} /> Premium
                </span>
              )}
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin" className="font-mono-caps text-[10px] text-secondary hover:underline">Admin</Link>
              )}
              <Link to="/profile" data-testid="nav-profile" className="font-mono-caps text-[10px] text-foreground hover:text-primary">
                {user.name?.split(" ")[0] || "You"}
              </Link>
              <button onClick={onLogout} data-testid="nav-logout" className="font-mono-caps text-[10px] text-muted-foreground hover:text-destructive">Out</button>
            </>
          ) : (
            <Link to="/login" data-testid="nav-login" className="bg-foreground text-background font-mono-caps text-[10px] px-4 py-2 hover-lift border border-foreground">
              Sign In
            </Link>
          )}
          <button aria-label={open ? "Close navigation" : "Open navigation"} aria-expanded={open} className="xl:hidden text-foreground" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="xl:hidden border-t border-white/10 bg-black px-5 py-3 flex flex-col gap-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`mobile-nav-${l.label.toLowerCase().split(" ").join("-")}`} onClick={() => setOpen(false)} className="font-mono-caps text-xs py-2 text-muted-foreground hover:text-primary">
              {l.label}
            </Link>
          ))}
        </div>
      )}
    </nav>
  );
}
